import React, { useState } from 'react'
import { Button, ConfigProvider, Form, message, Modal } from 'antd'
import { CopyOutlined, DeleteOutlined, EyeOutlined, FileSearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import MainTable from '../../components/Basic/MainTable'
import dayjs from 'dayjs'
import PopupConfirm from '../../components/Basic/PopupConfirm'
import { addIndicator, deleteIndicator, donwloadIndicator, fetchIndicator, modifyIndicator } from '../../apis/indicator'
import { useSelector } from 'react-redux'
import IndicatorForm from './IndicatorForm'
import { DataType } from '../../constants/DataType'
import IndicatorDownload from './IndicatorDownload'
import IndicatorDetails from './IndicatorDetails'

const { DATE, DATETIME } = DataType
function IndicatorSetting (props) {
  const { params, brandMapping, modalData, setModalData } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowGroup } = user || {}
  const { fetchParams, handleFetchParams } = params
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState([])
  const [counter, setCounter] = useState([])
  const tableMapping = dataSource?.reduce((all, i) => ({ ...all, [i.dataSourceId]: i.tables?.map(j => ({ ...j, dataSourceId: i.dataSourceId })) }), {})
  const tableColMapping = Object.values(tableMapping)?.flat()?.reduce((all, i) => ({ ...all, [`${i.dataSourceId}-${i.tableName}`]: i.columns }), {})
  const [crossDataColumn, setCrossDataColumn] = useState([])
  const dataSourceTableMapping = crossDataColumn?.reduce((all, i) => ({ ...all, [i.value]: i.children?.map(t => ({ ...t, value: t.value, dataSourceId: i.value })) }), {}) || []
  const tableJoinKeyMapping = Object.values(dataSourceTableMapping)?.flat()?.reduce((all, i) => ({ ...all, [`${i.dataSourceId}-${i.value}`]: i.children?.map(t => ({ ...t, tableName: i.value, value: t.value, label: t.label, dataSourceId: i.dataSourceId })) }), {}) || []
  const allTableKeyMapping = Object.values(tableJoinKeyMapping)?.flat()?.reduce((all, i) => ({ ...all, [`${i.dataSourceId}^@^${i.tableName}^@^${i.value}`]: i }), {}) || []

  const handleDelete = (id, data) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${ln_t('indicator.dimensionSetting')}`,
      content: `${ln_t('message.confirm.delete')} ${data.name}?`,
      onOk: async () => {
        const result = await deleteIndicator(id)
        if (result?.status === 204) {
          handleFetchParams('add', {})
          message.success(ln_t('message.success.delete'))
        }
      }
    })
  }

  const handleChangeStatus = async (record, update) => {
    const result = await modifyIndicator(record?.id, update)
    if (result?.status === 200) {
      message.success(ln_t('message.success.modify'))
      handleFetchParams('add', {})
    }
  }

  const handleView = (data, viewOnly) => setModalData({ open: true, type: 'view', viewOnly, data })

  const apiFunc = async () => fetchIndicator(fetchParams)
  const columns = [
    { title: ln_t('indicator.name'), dataIndex: 'name', sorter: true },
    { title: ln_t('util.brand'), dataIndex: 'brandId', sorter: true, render: e => brandMapping[e] || e },
    {
      title: ln_t('util.status'),
      dataIndex: 'status',
      sorter: true,
      render: (e, record) => <PopupConfirm type="status" checked={e} onConfirm={() => handleChangeStatus(record, { status: e ? 0 : 1 })} />
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', sorter: true, render: e => dayjs(e).format('YYYY-MM-DD') },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', sorter: true, render: e => dayjs(e).format('YYYY-MM-DD') },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, data) => <div className="action-btn">
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(data, true)} />
        <Button size="small" icon={<CopyOutlined />} onClick={() => handleView(data, false)} />
        <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(id, data)}/>
        <Button size="small" icon={<FileSearchOutlined />} onClick={() => setModalData({ open: true, type: 'details', data })}/>
      </div>
    }
  ]

  const handleModalClose = () => {
    setModalData({ open: false })
    form.resetFields()
    setCounter([])
  }

  const getCrossDataType = id => crossDataColumn?.find(i => i.value === id)?.dataSourceType
  const formatFilters = (items, mapping) => {
    const filterObj = {}
    const dateFilter = {}
    Object.entries(items || {})?.forEach(([keys, val]) => {
      const [key, filter] = keys?.split('-')
      if (!filter || filter !== 'column') return
      const value = items[`${key}-value`]
      const lastItemVal = Array.isArray(val) ? val[val.length - 1] : val
      const isDate = mapping[val] === DATE || mapping[val] === DATETIME || lastItemVal === 'm' || lastItemVal === 'd'
      dateFilter[key] = isDate
      filterObj[key] = {
        column: Array.isArray(val) ? [getCrossDataType(val[0]), ...val]?.join('^@^') : val,
        operator: items[`${key}-operator`],
        value: isDate
          ? dayjs(value).format(`YYYY-MM-DD${mapping[val] === DATETIME ? ' HH:mm:ss' : ''}`)
          : Array.isArray(value) ? value.join(',') : value
      }
    })
    return { filters: Object.values(filterObj), dateFilter }
  }
  const handleSave = async val => {
    setLoading(true)
    const { name, processingMethod, brandId, isMonthly, dataSourceId, tableName, columnName, aggregate, filterLogic, dimensions, indicator1, indicator2, operator, useBase, ...other } = val
    const columnOpt = tableColMapping[`${dataSourceId}-${tableName}`]?.map(i => ({ value: i.columnName, label: i.columnName, ...i }))
    const columnMapping = columnOpt?.reduce((all, i) => ({ ...all, [i.columnName]: i }), {}) || {}
    const isCrossData = dataSourceId === 'crossData'
    const { filters, dateFilter } = processingMethod === 'rule' ? formatFilters(other, isCrossData ? allTableKeyMapping : columnMapping) : {}
    const reqBody = {
      name,
      processingMethod,
      groupId: nowGroup,
      brandId,
      isMonthly: isMonthly ? 'yes' : 'no',
      rule: processingMethod === 'rule'
        ? {
            crossDataSourceId: isCrossData ? tableName : null,
            dataSourceId: isCrossData ? columnName[0] : dataSourceId,
            dataSourceType: isCrossData ? getCrossDataType(columnName[0]) : dataSource?.find(i => i.dataSourceId === dataSourceId)?.dataSourceType,
            tableName: isCrossData ? columnName[1] : tableName,
            columnName: isCrossData ? columnName[2] : columnName,
            aggregate,
            logic: filterLogic,
            filters
          }
        : { indicator1, indicator2, operator, useBase: useBase ? 1 : 0 },
      UISetting: { formSetting: val, counter, dateFilter },
      dimensionIds: dimensions
    }
    // if (reqBody) return console.log(reqBody)
    const res = await addIndicator(reqBody)
    if (res?.status === 200) {
      handleFetchParams('add', {})
      message.success(ln_t('message.success.add'))
      handleModalClose()
    }
    setLoading(false)
  }

  const handleDownload = async value => {
    setLoading(true)
    const { downloadFormat, order, timeInterval } = value || {}
    const date = timeInterval && timeInterval.length === 2 ? { startDate: dayjs(timeInterval[0]).format('YYYY-MM-DD'), endDate: dayjs(timeInterval[1]).format('YYYY-MM-DD') } : {}
    const reqBody = { id: modalData?.data?.id, brandId: modalData?.data?.brandId, type: downloadFormat, ...date, order }
    const res = await donwloadIndicator(reqBody)
    if (res?.status === 200) {
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      const disposition = res.headers['content-disposition']
      if (disposition && disposition.includes('filename=')) {
        link.download = disposition.split('filename=')[1].replace(/"/g, '')
      }
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      link.remove()
      handleModalClose()
    }
    setLoading(false)
  }

  const passProps = { ...props, form, handleModalClose, loading, dataSource, setDataSource, counter, setCounter, tableMapping, tableColMapping, setModalData, crossDataColumn, setCrossDataColumn, allTableKeyMapping }
  const modalConf = {
    default: { component: <IndicatorForm {...passProps} />, props: { title: ln_t('indicator.indicatorSetting'), width: 700, style: { top: '32px' } }, handleSubmit: handleSave },
    download: { component: <IndicatorDownload {...passProps} />, props: { title: ln_t('indicator.downloadIndicator') }, handleSubmit: handleDownload },
    details: { component: <IndicatorDetails {...passProps} />, props: { title: ln_t('indicator.indicatorSetting'), width: 1000, style: { top: '60px' } } }
  }
  const { component: modalComponent, props: modalProps, handleSubmit } = modalData.open ? (modalConf[modalData?.type] || modalConf.default) : {}

  return <>
    <MainTable
      columns={columns}
      rowKey="id"
      apiFunc={apiFunc}
      params={params}
    />

    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Modal
        open={modalData?.open || false}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
        {...modalProps}
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit} disabled={modalData?.viewOnly}>
          {modalComponent}
        </Form>
      </Modal>
    </ConfigProvider>
  </>
}

export default IndicatorSetting
